import { jsx } from "react/jsx-runtime";
import { forwardRef, useRef } from "react";
import styled, { css } from "styled-components";
import { textStyles } from "./textStyles.js";
import { token } from "./token.js";
import { MINIMUM_ROWS, MAXIMUM_ROWS } from "./normalizeRows.js";
import { useAutoHeight } from "./useAutoHeight.js";
import { mergeRefs } from "./mergeRefs.js";
import { makeInternalTokens } from "./makeInternalTokens.js";
const Textarea = forwardRef(({
  id,
  isDisabled = false,
  isReadOnly = false,
  isInvalid = false,
  onBlur,
  onChange,
  onFocus,
  placeholder,
  value,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  "aria-describedby": ariaDescribedBy,
  minRows = MINIMUM_ROWS,
  maxRows = MAXIMUM_ROWS,
  maxLength,
  autoFocus = false,
  ...props
}, ref) => {
  const internalRef = useRef(null);
  useAutoHeight(internalRef, minRows, maxRows);
  function handleOnChange(e) {
    if (onChange) {
      const newValue = e.currentTarget.value;
      onChange(newValue);
    }
  }
  return /* @__PURE__ */ jsx(TextareaStyled, {
    value,
    onChange: handleOnChange,
    id,
    disabled: isDisabled,
    readOnly: isReadOnly,
    $isInvalid: isInvalid,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": ariaDescribedBy,
    onBlur,
    onFocus,
    placeholder,
    autoComplete: "off",
    autoCorrect: "off",
    autoFocus,
    ref: mergeRefs(ref, internalRef),
    maxLength,
    ...props
  });
});
const {
  internalTokens,
  declareTokens
} = makeInternalTokens({
  lightTheme: {
    "textarea-fill": token.gray10,
    "textarea-fill-disabled": token.gray25,
    "textarea-border": token.gray100,
    "textarea-border-hover": token.gray200,
    "textarea-border-focus": token.gray300,
    "textarea-border-disabled": token.gray100,
    "textarea-shadow-hover": token.elevation1,
    "textarea-focus-ring": token.gray100
  },
  darkTheme: {
    "textarea-fill": "transparent",
    "textarea-fill-disabled": token.gray50a,
    "textarea-border": token.gray200a,
    "textarea-border-hover": token.gray300a,
    "textarea-border-focus": token.gray400a,
    "textarea-border-disabled": token.gray100a,
    "textarea-shadow-hover": "none",
    "textarea-focus-ring": token.gray300a
  }
});
const TextareaStyled = styled.textarea.withConfig({
  displayName: "TextareaStyled",
  componentId: "sc-atdkub-0"
})(["&&{", " display:block;box-sizing:border-box;width:100%;margin:0;padding:", " ", ";border:1px solid ", ";border-radius:", ";background:", ";color:", ";outline:none;font-family:", ";resize:none;transition:none;", " &::placeholder{color:", ";}&:hover{border-color:", ";box-shadow:", ";}&:focus{border-color:", ";box-shadow:0 0 0 3px ", ";}&:disabled{border-color:", ";background:", ";color:", ";box-shadow:none;&::placeholder{color:", ";}}", ";}"], declareTokens, token.spacing3, token.spacing5, internalTokens["textarea-border"], token.radiusBase, internalTokens["textarea-fill"], token.colorText, token.brandFontStackBase, textStyles.paragraphM, token.colorTextDisabled, internalTokens["textarea-border-hover"], internalTokens["textarea-shadow-hover"], internalTokens["textarea-border-focus"], internalTokens["textarea-focus-ring"], internalTokens["textarea-border-disabled"], internalTokens["textarea-fill-disabled"], token.colorTextMuted, token.colorTextDisabled, (props) => props.$isInvalid && css(["border-color:", ";&:hover{border-color:", ";}&:focus{border-color:", ";}"], token.red500, token.red600, token.red500));
export {
  Textarea
};
//# sourceMappingURL=Textarea.js.map
